import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { localize } from 'common/utils';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import * as S from './MenuEditorRoot.styles';
import CategoryActions from '../../pages/SharedMenuEditorPage/components/CategoryActions';
import { CollapseIcon } from '../../pages/SharedMenuEditorPage/components/CollapseIcon';
import Collection from '../../pages/SharedMenuEditorPage/components/Collections/Collection';
import OptionsCollection from '../../pages/SharedMenuEditorPage/components/Collections/OptionsCollection';
import SharedMenuNavigation from '../../pages/SharedMenuEditorPage/components/Navigation/SharedMenuNavigation';
import { Content } from '../CommonStyles';

type MenuEditorRootProps = {
  onClickAddCategory: () => void;
  onStartMenuBootstrap?: () => void;
  woltcomUrl?: string;
  isImageGalleryOpen: boolean;
  onCloseImageGallery: () => void;
};

export const MenuEditorRoot = ({
  onClickAddCategory,
  onStartMenuBootstrap = () => ({}),
  woltcomUrl,
  isImageGalleryOpen,
  onCloseImageGallery,
}: MenuEditorRootProps) => {
  const [isNarrowView, setIsNarrowView] = useState(false);
  const [collapsed, setCollapsed] = useState(isNarrowView);
  const menuPermissions = useOfferingMenuPermissionsContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const mainElementRef = useRef<HTMLDivElement>(null);
  const { path } = useRouteMatch();
  const theme = useTheme();
  const intl = useIntl();
  const { selection, categories } = useSelectionData();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = vlmeConfig?.isSharedVenue;

  useLayoutEffect(() => {
    if (!rootRef.current || !window.ResizeObserver) {
      // Ignore if `ResizeObserver` not available in the browser.
      return;
    }
    const resizeObserver = new ResizeObserver(entries => setIsNarrowView(entries[0]?.contentRect.width <= 960));
    resizeObserver.observe(rootRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isNarrowView) {
      setCollapsed(isNarrowView);
    }
  }, [isNarrowView]);

  return (
    <Content style={{ overflowY: 'auto' }}>
      <S.LeftNavigation>
        <S.NavigationContainer collapsed={collapsed}>
          {selection && <SharedMenuNavigation onClickAddCategory={onClickAddCategory} woltcomUrl={woltcomUrl} />}
        </S.NavigationContainer>
        <S.CollapseButton collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
          <CollapseIcon direction={theme.cbDirection} collapsed={collapsed} />
        </S.CollapseButton>
      </S.LeftNavigation>
      <Switch>
        <Route exact path={path} key="all-items">
          <S.Main ref={mainElementRef}>
            <Collection
              description={intl.formatMessage({ id: 'menu.allItemsDescription' })}
              listType="all-items"
              onStartMenuBootstrap={onStartMenuBootstrap}
              title={intl.formatMessage({ id: 'menu.allItems' })}
              isImageGalleryOpen={isImageGalleryOpen}
              onCloseImageGallery={onCloseImageGallery}
            />
          </S.Main>
        </Route>
        <Route exact path={`${path}/options`} key="options">
          <S.Main ref={mainElementRef}>
            <OptionsCollection
              description={intl.formatMessage({ id: 'menu.allOptionsDescription' })}
              onStartMenuBootstrap={onStartMenuBootstrap}
              title={intl.formatMessage({ id: 'menu.allOptions' })}
            />
          </S.Main>
        </Route>
        <Route exact path={`${path}/all-disabled`} key="all-disabled">
          <S.Main ref={mainElementRef}>
            <Collection
              description={intl.formatMessage({ id: 'menu.allDisabledDescription' })}
              listType="all-disabled"
              onStartMenuBootstrap={onStartMenuBootstrap}
              title={intl.formatMessage({ id: 'menu.allDisabled' })}
              isImageGalleryOpen={isImageGalleryOpen}
              onCloseImageGallery={onCloseImageGallery}
            />
          </S.Main>
        </Route>
        <Route exact path={`${path}/no-category`} key="no-category">
          <S.Main ref={mainElementRef}>
            <Collection
              description={intl.formatMessage({ id: 'menu.nonCategorisedDescription' })}
              listType="items-with-no-category"
              onStartMenuBootstrap={onStartMenuBootstrap}
              title={intl.formatMessage({ id: 'menu.nonCategorised' })}
              isImageGalleryOpen={isImageGalleryOpen}
              onCloseImageGallery={onCloseImageGallery}
            />
          </S.Main>
        </Route>
        {categories.map(category => (
          <Route exact path={`${path}/${category.id}`} key={category.id}>
            <S.Main ref={mainElementRef}>
              <Collection
                description={
                  category.description ? localize(category.description, selection.primary_language) : undefined
                }
                category={category}
                listType="items-by-category"
                onStartMenuBootstrap={onStartMenuBootstrap}
                rightContent={
                  isSharedVenue || !menuPermissions.contentEdit ? null : (
                    <CategoryActions type="category" category={category} />
                  )
                }
                title={localize(category.name, selection.primary_language)}
                isImageGalleryOpen={isImageGalleryOpen}
                onCloseImageGallery={onCloseImageGallery}
              />
            </S.Main>
          </Route>
        ))}
        {categories.flatMap(({ id: categoryId, subcategories }) =>
          subcategories?.map(subcategory => (
            <Route
              exact
              path={`${path}/${categoryId}/subcategories/${subcategory.id}`}
              key={categoryId + subcategory.id}
            >
              <S.Main ref={mainElementRef}>
                <Collection
                  description={
                    subcategory.description ? localize(subcategory.description, selection.primary_language) : undefined
                  }
                  category={subcategory}
                  listType="items-by-subcategory"
                  onStartMenuBootstrap={onStartMenuBootstrap}
                  rightContent={
                    isSharedVenue || !menuPermissions.contentEdit ? null : (
                      <CategoryActions type="subcategory" category={subcategory} />
                    )
                  }
                  title={localize(subcategory.name, selection.primary_language)}
                  isImageGalleryOpen={isImageGalleryOpen}
                  onCloseImageGallery={onCloseImageGallery}
                />
              </S.Main>
            </Route>
          )),
        )}
      </Switch>
    </Content>
  );
};
